import axios from "axios";
// axiosConfig has no default export — it attaches the request/response interceptors
// (Gandalf token, impersonation, 401 handling) to the global axios singleton. Import it
// here for that side-effect so these calls are always configured, independent of startup
// import-ordering.
import "../../utils/axiosConfig";
import { getAuthHeader } from "../../utils/authHeader";
import {
    CandidateStatus,
    MlsCandidateActionResponse,
    MlsCandidateAddResponse,
    MlsCandidateListResponse,
    MlsCandidateRefreshResponse,
} from "../../types/mlsCandidate";

// User-facing select-or-add (MB-1313) calls. The interceptor does not attach the Firebase /
// impersonation auth header, so getAuthHeader() still supplies it per call.
const candidatesUrl = (): string =>
    `${process.env.REACT_APP_BACKEND_URL}/listing/mls-candidates`;

export const getMlsCandidates = async (
    status: CandidateStatus,
): Promise<MlsCandidateListResponse> => {
    const headers = await getAuthHeader();
    const response = await axios.get<MlsCandidateListResponse>(candidatesUrl(), {
        headers,
        params: { status },
        withCredentials: true,
    });
    return response.data;
};

export const addMlsCandidates = async (
    listingKeys: string[],
): Promise<MlsCandidateAddResponse> => {
    const headers = await getAuthHeader();
    const response = await axios.post<MlsCandidateAddResponse>(
        `${candidatesUrl()}/add`,
        { listingKeys },
        { headers, withCredentials: true },
    );
    return response.data;
};

export const discardMlsCandidates = async (
    listingKeys: string[],
): Promise<MlsCandidateActionResponse> => {
    const headers = await getAuthHeader();
    const response = await axios.post<MlsCandidateActionResponse>(
        `${candidatesUrl()}/discard`,
        { listingKeys },
        { headers, withCredentials: true },
    );
    return response.data;
};

export const restoreMlsCandidates = async (
    listingKeys: string[],
): Promise<MlsCandidateActionResponse> => {
    const headers = await getAuthHeader();
    const response = await axios.post<MlsCandidateActionResponse>(
        `${candidatesUrl()}/restore`,
        { listingKeys },
        { headers, withCredentials: true },
    );
    return response.data;
};

// On-demand "Refresh": re-discovers the agent's active listings and stages them all for
// review (review-only — never auto-adds). Returns the pending (tickable) count.
export const refreshMlsCandidates =
    async (): Promise<MlsCandidateRefreshResponse> => {
        const headers = await getAuthHeader();
        const response = await axios.post<MlsCandidateRefreshResponse>(
            `${candidatesUrl()}/refresh`,
            {},
            { headers, withCredentials: true },
        );
        return response.data;
    };
